import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import { useTheme } from "emotion-theming";

import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { ApplicationContext } from "wrapper/ApplicationWrapper/context";

import { getAvailableOffers } from "layouts/Payment/common/dependencies/utils";
import { getStringFromId } from "../../../../../helpers/vernacular";
import { doRedirect } from "helpers/utils/redirection";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";

import {
  VERNACULAR_DEFAULT_MESSAGES,
  VERNACULAR_STRING_IDS,
} from "shared/constants/vernacularConstants";
import { allTrackingHandlerForApps } from "helpers/utils/trackingHandler";
import { TRACKING_EVENTS } from "../../../../../constants";
import { trackOtherPaymentsClick, trackPaymentFailureBottomSheetActions } from "layouts/Payment/common/dependencies/tracking";
import { doAppendQueryParam } from "../../../../../helpers/index";
import { OtherPaymentConfig } from "@nykaa/payment-sdk/src/OtherPayment/@types";
import { CLIENT_URLS } from "constants/urls";
import { PAYMENT_OPTION } from "constants/paymentScreens";
import OtherPayment from "@nykaa/payment-sdk/src/OtherPayment";
import { checkSinglePaymentPageEligibility } from "layouts/CheckoutPayment/helpers";
import { offerReversal } from "store/payment/services";
import { ExperimentAndSwitchContext } from "wrapper/ExperimentAndSwitchWrapper/context";


interface IMorePaymentMethod {
  isVisible: boolean;
  paymentFailureFlow?: boolean;
  extraClass?: string;
  handleSuccessRedirection?: () => Promise<boolean | undefined>
  setShowFallbackModeSheet?: (data: boolean)=> void;
}

export const MorePaymentMethod = ({
  isVisible,
  paymentFailureFlow = false,
  handleSuccessRedirection,
  setShowFallbackModeSheet,
}: IMorePaymentMethod) => {
  const theme: Theme = useTheme();
  const history = useHistory();
  const paymentContext = useContext(PaymentContext);
  const { applicationData } = useContext(ApplicationContext);
  const { OfferReversal } = useContext(ExperimentAndSwitchContext);

  const { deviceType, isApp } = applicationData;
  const paymentMethodRule = paymentContext?.cartRules?.paymentRules;
  const availablePaymentMethods = paymentContext?.cartInfo?.availablePaymentMethods;
  const flow = paymentContext?.flow;
  const setPaymentResponseV2Data = paymentContext?.setPaymentResponseV2Data;
  const paymentResponseV2Data = paymentContext?.paymentResponseV2Data;
  const orderId = paymentResponseV2Data?.orderId;
  const previousPaymentMode = paymentResponseV2Data?.paymentMode;
  const setPaymentOption = paymentContext.setPaymentOption;
  const setLoading = paymentContext?.setLoading;
  const availableOffers = getAvailableOffers(availablePaymentMethods, paymentMethodRule);
  const showAllPaymentMethods = checkSinglePaymentPageEligibility({ flow });

  const clearPaymentResponseV2Data = () => {
    setPaymentResponseV2Data({
      ...paymentResponseV2Data,
      paymentStatus: "",
      masterPaymentStatus: "",
      showFallbackModes: false,
      paymentMode: "",
      orderId: "",
    });
  };

  const handleMorePaymentClick = async () => {
    setLoading(true);
    let offerReversalParam = {
      orderId: orderId || "",
      mode: previousPaymentMode,
      deviceType,
    };

    let offerReversalApiCall: any = () => Promise.resolve();
    if (OfferReversal.experimentEnabled && paymentFailureFlow) {
      offerReversalApiCall = offerReversal;
    }

    /**
     * calling offer Reversal api before redirection
     */
     offerReversalApiCall(offerReversalParam).finally(async () => {
      setLoading(false);
      /**
       * When user click on other payment option
       * then setting payment option as New
       * this is for identity if user select new or saved payment option.
       */
      setPaymentOption(PAYMENT_OPTION.NEW);

      let offersLength = 0;
      if (availableOffers && availableOffers.length) {
        offersLength = availableOffers.length;
      }
      /**
       * Added for tracking for Apps and web
       */
      if (paymentFailureFlow) {
        let noSuccessRedirection = await handleSuccessRedirection?.();
        if (noSuccessRedirection) {
          const query = new URLSearchParams(history.location.search);
          // query is used if in case user refreshes the page
          const failureOrderId = orderId || query.get("paymentFailureOrderId");
          // redirecting user to fresh payment method screen
          let redirectionUrl = `${CLIENT_URLS.PAYMENT_BASE_URL}?paymentFailureOrderId=${failureOrderId}&screen=newPayment`;
          // when showing new and saved payment method on single screen.
          if (showAllPaymentMethods) {
            redirectionUrl = `${CLIENT_URLS.PAYMENT_BASE_URL}?paymentFailureOrderId=${failureOrderId}`;
          }

          clearPaymentResponseV2Data();
          history.replace(redirectionUrl);

          const eventName = TRACKING_EVENTS.FAILURE_BOTTOMSHEET_OTHER_PAYMENT_CLICK;
          allTrackingHandlerForApps({ event: eventName, isApp });
          //tracking for web
          trackPaymentFailureBottomSheetActions({ event: eventName });
          setShowFallbackModeSheet?.(false);
          return;
          // window.location.href = window.location.href?.toString().replace(/showFallbackBottomSheet=true/, 'screen=newPayment')
        }
      } else {
        trackOtherPaymentsClick(deviceType, offersLength, flow);
        allTrackingHandlerForApps({ event: TRACKING_EVENTS.OTHER_PAYMENT_METHOD_CLICK, isApp });
      }

      let url = doAppendQueryParam("screen", "newPayment");
      doRedirect({
        url,
        history,
        isClientRedirection: true,
      });
    });
  };

  const title = getStringFromId(
    VERNACULAR_STRING_IDS.OTHER_PAYMENT_OPTIONS_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.OTHER_PAYMENT_OPTIONS_TITLE
  );
  const subTitle = getStringFromId(
    VERNACULAR_STRING_IDS.OTHER_PAYMENT_OPTIONS_SUBTITLE,
    VERNACULAR_DEFAULT_MESSAGES.OTHER_PAYMENT_OPTIONS_SUBTITLE
  );

  const config: OtherPaymentConfig = {
    title,
    subTitle,
    onClick: handleMorePaymentClick,
    section: true,
    endIcon: getIcons("", ICONS_NAMES.ARROW_RIGHT),
    theme,
  };
  /**
   * css and config changes for paymentFailureFlow
   * as it does not require any subTitle, border, margin
   * but leadingIcon need to be added
   */
  if (paymentFailureFlow) {
    config.subTitle = "";
    config.leadingIcon = getIcons("", ICONS_NAMES.OTHER_PAYMENT_ICON);
    config.noBorder = true;
    config.noMargin = true;
    config.borderRadius = theme?.borders?.radiusPrimary;
  }

  return isVisible ? (
    <div className={paymentFailureFlow ? "" : "mrl20 ptb12"}>
      <OtherPayment {...config} />
    </div>
  ) : null;
};
